// Hämtar inloggad användares roll och behörighet i ett projekt.
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ProjectRole } from "@/lib/basProcess/roleViews";
import { PROJECT_ROLES, isReadOnlyRole } from "@/lib/basProcess/roleViews";
import { parseRolesInSystem } from "@/lib/basProcess/soloMode";

export interface ProjectRoleState {
  loading: boolean;
  isOwner: boolean;
  role: ProjectRole;
  canEdit: boolean;
  canExport: boolean;
  canInvite: boolean;
  canShareExternal: boolean;
  /** Rollen är granskande (Projektledare/Uppdragsledare) – endast läsa. */
  isReviewer: boolean;
  /** Roller som (utöver ägarens egen) arbetar i systemet i det här projektet. */
  rolesInSystem: ProjectRole[];
  /** Ägaren kan förhandsgranska processen som en annan roll. */
  setPreviewRole: (role: ProjectRole) => void;
  refresh: () => void;
}

const PREVIEW_KEY = "basprocess.previewRole";

export const useProjectRole = (projectId?: string): ProjectRoleState => {
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [assignedRole, setAssignedRole] = useState<ProjectRole>("Bas-P");
  const [canEdit, setCanEdit] = useState(false);
  const [canExport, setCanExport] = useState(false);
  const [canInvite, setCanInvite] = useState(false);
  const [canShareExternal, setCanShareExternal] = useState(false);
  const [rolesInSystem, setRolesInSystem] = useState<ProjectRole[]>([]);
  const [preview, setPreview] = useState<ProjectRole | null>(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(PREVIEW_KEY) : null;
    return (PROJECT_ROLES as string[]).includes(saved || "") ? (saved as ProjectRole) : null;
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!projectId) return;
      setLoading(true);
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      const [{ data: project }, { data: share }] = await Promise.all([
        supabase
          .from("projects")
          .select("user_id, owner_role, roles_in_system")
          .eq("id", projectId)
          .maybeSingle(),
        uid
          ? supabase
              .from("project_shares")
              .select("project_role, permission_level, can_export, can_invite, can_share_external")
              .eq("project_id", projectId)
              .eq("shared_with_id", uid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (!active) return;
      const owner = !!uid && project?.user_id === uid;
      setIsOwner(owner);
      setRolesInSystem(
        parseRolesInSystem((project as { roles_in_system?: unknown } | null)?.roles_in_system)
      );
      if (owner) {
        // Ägaren väljer själv sin roll; byggherre är standard tills något annat valts.
        const chosen = (project as { owner_role?: string | null } | null)?.owner_role;
        setAssignedRole(
          (PROJECT_ROLES as string[]).includes(chosen || "")
            ? (chosen as ProjectRole)
            : "Byggherre"
        );
        setCanEdit(true);
        setCanExport(true);
        setCanInvite(true);
        setCanShareExternal(true);
      } else if (share) {
        const r = (share as { project_role?: string }).project_role;
        setAssignedRole((PROJECT_ROLES as string[]).includes(r || "") ? (r as ProjectRole) : "Bas-U");
        const perms = share as {
          permission_level?: string;
          can_export?: boolean | null;
          can_invite?: boolean | null;
          can_share_external?: boolean | null;
        };
        setCanEdit(perms.permission_level !== "read");
        setCanExport(Boolean(perms.can_export));
        setCanInvite(Boolean(perms.can_invite));
        setCanShareExternal(Boolean(perms.can_share_external));
      } else {
        setCanEdit(false);
        setCanExport(false);
        setCanInvite(false);
        setCanShareExternal(false);
      }
      setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, [projectId, tick]);

  const setPreviewRole = useCallback((role: ProjectRole) => {
    setPreview(role);
    window.localStorage.setItem(PREVIEW_KEY, role);
  }, []);

  const effectiveRole = isOwner && preview ? preview : assignedRole;
  const readOnly = isReadOnlyRole(effectiveRole);

  return {
    loading,
    isOwner,
    role: effectiveRole,
    canEdit: readOnly ? false : canEdit,
    canExport,
    canInvite: readOnly ? false : canInvite,
    canShareExternal: readOnly ? false : canShareExternal,
    isReviewer: readOnly,
    rolesInSystem,
    setPreviewRole,
    refresh: () => setTick((t) => t + 1),
  };
};

export default useProjectRole;
